import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, Hammer, History, StickyNote } from 'lucide-react';
import '../components/delivery/delivery.css';
import '../components/upgrades/upgrades.css';
import ConfirmModal from '../components/ConfirmModal';
import { StatusBadge } from '../components/upgrades/StatusBadge';
import UpgradeStatusBar from '../components/upgrades/UpgradeStatusBar';
import UpgradeScopeCard from '../components/upgrades/UpgradeScopeCard';
import UpgradeMachinesCard from '../components/upgrades/UpgradeMachinesCard';
import UpgradeItemsCard from '../components/upgrades/UpgradeItemsCard';
import UpgradeScheduleCard from '../components/upgrades/UpgradeScheduleCard';
import UpgradeProgressCard from '../components/upgrades/UpgradeProgressCard';
import { ACTION_LABELS, FIELD_LABELS, UPGRADE_STATUS, CLOSED_STATUSES } from '../constants/upgrades';
import { api, czDateTime, money, computeTotals, dateRange } from '../utils/upgradesApi';

// Editable copy of the job; items get a client key so new rows can be tracked before they have an id
const toForm = (job) => ({
  title: job.title || '', description: job.description || '', internal_notes: job.internal_notes || '', completion_notes: job.completion_notes || '',
  location: job.location || '', site_id: job.site_id || null, contact_name: job.contact_name || '', contact_phone: job.contact_phone || '', contact_email: job.contact_email || '',
  planned_start: job.planned_start || '', planned_end: job.planned_end || '', arrival_time: job.arrival_time || '',
  quote_valid_until: job.quote_valid_until || '', discount_percent: job.discount_percent ?? 0,
  machine_ids: job.machines.map((m) => m.machine_id),
  crew: job.crew.map((c) => ({ user_id: c.user_id, is_lead: c.is_lead })),
  items: job.items.map((i) => ({ _key: `i${i.id}`, id: i.id, kind: i.kind, description: i.description, qty: i.qty, unit: i.unit, unit_price: i.unit_price })),
});

// the client key is only for React and for tracking new rows; it never goes to the server or into the dirty check
const withoutKey = (item) => Object.fromEntries(Object.entries(item).filter(([k]) => k !== '_key'));

const normalized = (form) => ({ ...form, items: form.items.map(withoutKey) });
const snapshot = (form) => JSON.stringify(normalized(form));

// Only the fields that differ from the loaded job are sent, so two people editing different parts do not overwrite each other
function toPayload(form, base) {
  const before = normalized(base);
  const after = normalized(form);
  const patch = {};
  for (const key of Object.keys(after)) {
    if (JSON.stringify(after[key]) !== JSON.stringify(before[key])) patch[key] = after[key];
  }
  if ('site_id' in patch) patch.site_id = patch.site_id || null;
  if ('discount_percent' in patch) patch.discount_percent = Number(patch.discount_percent) || 0;
  if (patch.items) patch.items = patch.items.map((i) => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price) }));
  return patch;
}

const UpgradeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [meta, setMeta] = useState({ users: [], templates: [], me: {} });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyItem, setBusyItem] = useState(null);
  const [error, setError] = useState('');
  const [problems, setProblems] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const applyJob = useCallback((data) => {
    const next = toForm(data);
    setJob(data);
    setForm(next);
    setBaseline(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api(`/${id}`), api('/meta')])
      .then(([data, m]) => { if (!cancelled) { applyJob(data); setMeta(m); } })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [id, applyJob]);

  const dirty = form !== null && baseline !== null && snapshot(form) !== snapshot(baseline);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    // the router is not a data router (no useBlocker), so links inside the app are guarded here, before React Router sees the click
    const guardLinks = (e) => {
      const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
      const href = link?.getAttribute('href') || '';
      if (!link || link.target === '_blank' || link.hasAttribute('download') || !href.startsWith('/') || href.startsWith('/api/')) return;
      if (!window.confirm('Máte neuložené změny. Opravdu odejít bez uložení?')) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener('beforeunload', warn);
    document.addEventListener('click', guardLinks, true);
    return () => {
      window.removeEventListener('beforeunload', warn);
      document.removeEventListener('click', guardLinks, true);
    };
  }, [dirty]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const fail = (e) => { setError(e.message); setProblems(e.data?.problems || []); };
  const reload = useCallback(async () => { applyJob(await api(`/${id}`)); }, [id, applyJob]);

  const save = async () => {
    setSaving(true);
    setError('');
    setProblems([]);
    try { applyJob(await api(`/${id}`, { method: 'PATCH', body: toPayload(form, baseline) })); } catch (e) { fail(e); } finally { setSaving(false); }
  };

  const transition = async (to, extra = {}) => {
    setBusy(true);
    setError('');
    setProblems([]);
    try { applyJob(await api(`/${id}/status`, { method: 'POST', body: { status: to, ...extra } })); } catch (e) { fail(e); } finally { setBusy(false); }
  };

  // Ticking is saved at once; the unsaved form is left alone (only the server copy of the job is refreshed)
  const toggleItem = async (itemId, done) => {
    setBusyItem(itemId);
    setError('');
    try { setJob(await api(`/${id}/items/${itemId}`, { method: 'PATCH', body: { done } })); } catch (e) { fail(e); } finally { setBusyItem(null); }
  };

  const remove = async () => {
    setConfirmDelete(false);
    try { await api(`/${id}`, { method: 'DELETE' }); navigate('/upravy'); } catch (e) { fail(e); }
  };

  if (!job || !form) {
    return (
      <div className="dlv upg-wrap">
        {error ? <p role="alert" className="upg-error">{error} <Link className="upg-link" to="/upravy">Zpět na seznam</Link></p> : <p className="dlv-hint">Načítám…</p>}
      </div>
    );
  }

  const locked = CLOSED_STATUSES.includes(job.status);
  const canDelete = ['admin', 'manager'].includes(meta.me?.role) && ['navrh', 'zruseno'].includes(job.status);
  // the price in the hero follows the unsaved form, so it is right while the quote is being edited
  const totals = computeTotals(form.items, form.discount_percent, job.vat_rate);

  return (
    <div className="dlv upg-wrap">
      <Link to="/upravy" className="upg-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12, fontWeight: 600 }}><ArrowLeft size={16} /> Všechny úpravy</Link>

      <div className="dlv-hero">
        <div className="dlv-hero__kicker">Úprava u zákazníka · {job.number}</div>
        <h1 className="dlv-hero__title">{job.title}</h1>
        <div className="dlv-hero__meta">
          <span><strong>{job.customer_name}</strong></span>
          {job.machines.map((m) => <span key={m.machine_id}>{m.model_type} · SN <strong>{m.serial_number}</strong></span>)}
          <span>{dateRange(job)}</span>
        </div>
        <div className="dlv-hero__badge">
          <StatusBadge status={job.status} />
          <b style={{ display: 'block', marginTop: 8 }}>{money(totals.gross)}</b>
          <span style={{ marginTop: 0 }}>s DPH</span>
        </div>
      </div>

      <div className="dlv-card" style={{ marginTop: 16 }}>
        <UpgradeStatusBar job={job} dirty={dirty} busy={busy} onTransition={transition} />
      </div>

      {error && <p role="alert" className="upg-error">{error}</p>}
      {problems.length > 0 && (
        <ul className="upg-problems" aria-label="Co ještě chybí">{problems.map((p) => <li key={p.code}>{p.message}</li>)}</ul>
      )}

      <div className="upg-gap" style={{ marginTop: 16 }}>
        <div className="upg-grid2">
          <UpgradeScopeCard job={job} form={form} set={set} locked={locked} />
          <div className="upg-gap">
            <UpgradeMachinesCard job={job} form={form} set={set} locked={locked} />
            <UpgradeScheduleCard job={job} form={form} set={set} users={meta.users} locked={locked} dirty={dirty} onError={setError} />
          </div>
        </div>

        <UpgradeItemsCard job={job} form={form} set={set} locked={locked} dirty={dirty}
          templates={meta.templates} onReload={reload} onError={setError} />

        <div className="upg-grid2">
          <UpgradeProgressCard job={job} form={form} set={set} onToggle={toggleItem} busyItem={busyItem} />
          <section className="dlv-card" aria-labelledby="upg-notes">
            <h2 id="upg-notes" className="upg-card-title"><StickyNote size={18} /> Interní poznámka</h2>
            <label className="dlv-label" htmlFor="upg-internal">Vidí jen kolegové, na nabídku se netiskne</label>
            <textarea id="upg-internal" className="dlv-input upg-textarea" value={form.internal_notes} onChange={(e) => set('internal_notes', e.target.value)} />
            <div style={{ marginTop: 14 }}>
              <span className="dlv-label">Zákazník</span>
              <Link className="upg-link" to={`/customers/${job.customer_id}`}>{job.customer_name}</Link>
              {job.distance_km != null && <span className="upg-muted"> · cca {job.distance_km} km od základny</span>}
            </div>
          </section>
        </div>

        <section className="dlv-card" aria-labelledby="upg-history">
          <h2 id="upg-history" className="upg-card-title"><History size={18} /> Historie</h2>
          {job.history.length === 0 ? <p className="dlv-hint">Zatím žádné záznamy.</p> : (
            <ul className="upg-history">
              {job.history.map((h) => (
                <li key={h.id}>
                  <time dateTime={h.created_at}>{czDateTime(h.created_at)}</time>
                  <span><strong>{h.user_name || 'systém'}</strong> · {historyText(h)}</span>
                </li>
              ))}
            </ul>
          )}
          {canDelete && (
            <button type="button" className="dlv-btn upg-btn-danger upg-sm" style={{ marginTop: 14 }} onClick={() => setConfirmDelete(true)}><Trash2 size={15} /> Smazat úpravu</button>
          )}
        </section>
      </div>

      {dirty && (
        <div className="dlv-nav upg-savebar" role="region" aria-label="Neuložené změny">
          <button type="button" className="dlv-btn dlv-btn--primary" disabled={saving} onClick={save}><Save size={16} /> {saving ? 'Ukládám…' : 'Uložit změny'}</button>
          <button type="button" className="dlv-btn" disabled={saving} onClick={() => { setForm(baseline); setError(''); }}>Zahodit</button>
          <span className="upg-savebar__text"><Hammer size={14} style={{ verticalAlign: '-2px' }} /> Máte neuložené změny</span>
        </div>
      )}

      <ConfirmModal show={confirmDelete} title="Smazat úpravu?" message={`Úprava ${job.number} bude trvale smazána včetně položek a posádky.`} confirmText="Smazat" danger
        onConfirm={remove} onCancel={() => setConfirmDelete(false)} />
    </div>
  );
};

function historyText(h) {
  const v = h.new_values || {};
  if (h.action === 'status' && v.to) {
    return `${ACTION_LABELS.status}: ${UPGRADE_STATUS[v.from]?.label || '—'} → ${UPGRADE_STATUS[v.to]?.label || v.to}${v.auto ? ' (automaticky)' : ''}`;
  }
  if (h.action === 'update' && v.fields) return `${ACTION_LABELS.update}: ${v.fields.map((f) => FIELD_LABELS[f] || f).join(', ')}`;
  return ACTION_LABELS[h.action] || h.action;
}

export default UpgradeDetail;
